/**
 * PHARMAKON — Fournisseur ERP · « Réclamations reçues » API (refonte WP-34 server / WP-36 screens;
 * D-20, A-20, OP-03-31; design/ux/03_avoirs_bc_reclamations.md §5.8, §5.9)
 *
 *   GET  /fournisseur-portail/reclamations                 the org's received réclamations: views with
 *                                                          counts, filters (pharmacie, motif, période,
 *                                                          search), server sort and page
 *   GET  /fournisseur-portail/reclamations/badge           the menu badge `{ total, a_traiter, non_lues }`
 *   GET  /fournisseur-portail/reclamations/pharmacies      the « Pharmacie » filter (who sent the org one)
 *   GET  /fournisseur-portail/reclamations/:id             the document and the shared thread (marks it read)
 *   POST /fournisseur-portail/reclamations/:id/prendre-en-charge
 *   POST /fournisseur-portail/reclamations/:id/messages    `{ message }` — « Répondre »
 *   POST /fournisseur-portail/reclamations/:id/accepter    `{ message? }` → Résolue
 *   POST /fournisseur-portail/reclamations/:id/proposer    `{ lignes: [{ ligne_reclamation_id, quantite }], montant?, message }`
 *   POST /fournisseur-portail/reclamations/:id/rejeter     `{ motif }` → Rejetée
 *   POST /fournisseur-portail/reclamations/:id/pieces-jointes          multipart `fichiers`
 *   GET  /fournisseur-portail/reclamations/:id/pieces-jointes/:pid     the bytes (Blob)
 *
 * The org is the principal's — never sent by the client. The client resolves with the BODY.
 */
import api from "../../services/api.client.js";
import { corpsErreurBlob } from "@/lib/blob.js";

const BASE = "/fournisseur-portail/reclamations";
const enc = (v) => encodeURIComponent(v);
const doc = (rid) => `${BASE}/${enc(rid)}`;

export const reclamationsRecuesService = Object.freeze({
  /** `{ vue?, pharmacie_id?, motif?, statut?, search?, date_debut?, date_fin?, sort_by?, sort_order?, page?, limit? }` */
  list: (params = {}, opts = {}) => api.get(BASE, { params, signal: opts.signal }),
  badge: (opts = {}) => api.get(`${BASE}/badge`, { signal: opts.signal }),
  pharmacies: (opts = {}) => api.get(`${BASE}/pharmacies`, { signal: opts.signal }),
  getById: (rid, opts = {}) => api.get(doc(rid), { signal: opts.signal }),
  prendreEnCharge: (rid) => api.post(`${doc(rid)}/prendre-en-charge`, {}),
  message: (rid, message) => api.post(`${doc(rid)}/messages`, { message }),
  /** `message` optional (the resolution's words; the server writes a default one without it). */
  accepter: (rid, message) => api.post(`${doc(rid)}/accepter`, message ? { message } : {}),
  /** `{ lignes: [{ ligne_reclamation_id, quantite }], montant?: string|null, message }` */
  proposer: (rid, body) => api.post(`${doc(rid)}/proposer`, body),
  rejeter: (rid, motif) => api.post(`${doc(rid)}/rejeter`, { motif }),
  /** `fichiers`: File[] — one multipart request (field `fichiers`). */
  ajouterPieces: (rid, fichiers) => {
    const fd = new FormData();
    for (const f of fichiers) fd.append("fichiers", f, f.name);
    return api.post(`${doc(rid)}/pieces-jointes`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  },
  /** The bytes of an attachment (authenticated — never a token in a URL). @returns {Promise<Blob>} */
  piece: async (rid, pieceId) => {
    try {
      return await api.get(`${doc(rid)}/pieces-jointes/${enc(pieceId)}`, { responseType: "blob" });
    } catch (e) {
      throw await corpsErreurBlob(e);
    }
  },
});

export default reclamationsRecuesService;
